import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid, Alert,
  CircularProgress, FormControlLabel, Switch, MenuItem,
  IconButton, Card, CardContent, Avatar, Chip
} from '@mui/material';
import { Save, ArrowBack, Add, Delete, Person, Visibility } from '@mui/icons-material';
import api from '../services/api';

const emptyPresenter = {
  name: '',
  title: '',
  institution: '',
  specialty: '',
  image: ''
};

const emptyReplay = {
  title: '',
  description: '',
  date: new Date().toISOString().split('T')[0],
  videoUrl: '',
  youtubeId: '',
  thumbnailUrl: '',
  durationFormatted: '',
  category: '',
  specialty: '',
  language: 'fr',
  tagsInput: '',
  sessionId: '',
  presenters: [{ ...emptyPresenter }],
  isFeatured: false,
  isPublished: false
};

export default function ReplayEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sessions, setSessions] = useState([]);
  const [viewCount, setViewCount] = useState(0);

  const [replay, setReplay] = useState({ ...emptyReplay });

  useEffect(() => {
    fetchSessions();
    if (!isNew) {
      fetchReplay();
    }
  }, [id]);

  const fetchSessions = async () => {
    try {
      const res = await api.get('/sessions');
      setSessions(res.data);
    } catch (err) {
      // La liste des sessions est un confort (lien optionnel) - pas bloquant
    }
  };

  const fetchReplay = async () => {
    try {
      // Utilise la liste admin (/replays/all) plutot que GET /replays/:id :
      // cette derniere est la route publique de lecture, qui incremente
      // viewCount a chaque appel - l'ouvrir pour editer ne doit pas compter
      // comme une vue.
      const res = await api.get('/replays/all');
      const data = res.data.find((r) => r._id === id);
      if (!data) {
        setError('Replay introuvable');
        return;
      }
      setViewCount(data.viewCount || 0);
      setReplay({
        ...emptyReplay,
        ...data,
        date: data.date ? new Date(data.date).toISOString().split('T')[0] : '',
        sessionId: data.sessionId?._id || data.sessionId || '',
        tagsInput: (data.tags || []).join(', '),
        presenters: data.presenters?.length > 0 ? data.presenters : [{ ...emptyPresenter }]
      });
    } catch (err) {
      setError('Erreur lors du chargement du replay');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setReplay({ ...replay, [field]: value });
  };

  const handlePresenterChange = (index, field, value) => {
    const updated = [...replay.presenters];
    updated[index] = { ...updated[index], [field]: value };
    setReplay({ ...replay, presenters: updated });
  };

  const addPresenter = () => {
    setReplay({ ...replay, presenters: [...replay.presenters, { ...emptyPresenter }] });
  };

  const removePresenter = (index) => {
    if (replay.presenters.length <= 1) return;
    setReplay({ ...replay, presenters: replay.presenters.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const { tagsInput, ...rest } = replay;
      const payload = {
        ...rest,
        sessionId: replay.sessionId || undefined,
        tags: tagsInput.split(',').map((t) => t.trim()).filter(Boolean),
        presenters: replay.presenters.filter((p) => p.name.trim())
      };

      if (isNew) {
        await api.post('/replays', payload);
        setSuccess('Replay cree avec succes');
      } else {
        await api.put(`/replays/${id}`, payload);
        setSuccess('Replay mis a jour');
      }

      setTimeout(() => navigate('/replays'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/replays')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {isNew ? 'Nouveau Replay' : 'Modifier le Replay'}
        </Typography>
        {!isNew && (
          <Chip
            icon={<Visibility />}
            label={`${viewCount} vue(s)`}
            variant="outlined"
          />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Informations generales</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Titre du replay"
                value={replay.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={replay.date}
                onChange={(e) => handleChange('date', e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={replay.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="URL video"
                value={replay.videoUrl || ''}
                onChange={(e) => handleChange('videoUrl', e.target.value)}
                placeholder="https://youtube.com/watch?v=... ou lien MinIO"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID YouTube"
                value={replay.youtubeId || ''}
                onChange={(e) => handleChange('youtubeId', e.target.value)}
                placeholder="dQw4w9WgXcQ (optionnel si URL video renseignee)"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="URL Miniature"
                value={replay.thumbnailUrl || ''}
                onChange={(e) => handleChange('thumbnailUrl', e.target.value)}
                placeholder="https://..."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Duree"
                value={replay.durationFormatted || ''}
                onChange={(e) => handleChange('durationFormatted', e.target.value)}
                placeholder="45:30"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Categorie"
                value={replay.category || ''}
                onChange={(e) => handleChange('category', e.target.value)}
                placeholder="Conference, Tutoriel, Debat..."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Specialite"
                value={replay.specialty || ''}
                onChange={(e) => handleChange('specialty', e.target.value)}
                placeholder="Cardiologie, Neurologie..."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Tags"
                value={replay.tagsInput}
                onChange={(e) => handleChange('tagsInput', e.target.value)}
                placeholder="Diagnostic, Recherche (separes par des virgules)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Session liee (optionnel)"
                value={replay.sessionId || ''}
                onChange={(e) => handleChange('sessionId', e.target.value)}
                helperText="Permet d'afficher un lien 'Voir la session complete' depuis le replay"
              >
                <MenuItem value="">Aucune session liee</MenuItem>
                {sessions.map((s) => (
                  <MenuItem key={s._id} value={s._id}>
                    {s.title} — {new Date(s.date).toLocaleDateString('fr-FR')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={replay.isPublished}
                    onChange={(e) => handleChange('isPublished', e.target.checked)}
                  />
                }
                label="Publie (visible sur le site)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={replay.isFeatured}
                    onChange={(e) => handleChange('isFeatured', e.target.checked)}
                  />
                }
                label="Mettre a la une"
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
              Presentateurs ({replay.presenters.length})
            </Typography>
            <Button startIcon={<Add />} onClick={addPresenter} size="small">
              Ajouter
            </Button>
          </Box>

          {replay.presenters.map((presenter, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Presentateur {index + 1}
                  </Typography>
                  {replay.presenters.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => removePresenter(index)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={1}>
                    <Avatar src={presenter.image} sx={{ width: 48, height: 48 }}>
                      {presenter.name?.charAt(0)}
                    </Avatar>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Titre"
                      value={presenter.title}
                      onChange={(e) => handlePresenterChange(index, 'title', e.target.value)}
                      placeholder="Dr / Pr"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nom complet"
                      value={presenter.name}
                      onChange={(e) => handlePresenterChange(index, 'name', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Institution"
                      value={presenter.institution}
                      onChange={(e) => handlePresenterChange(index, 'institution', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Specialite"
                      value={presenter.specialty}
                      onChange={(e) => handlePresenterChange(index, 'specialty', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <TextField
                      fullWidth
                      size="small"
                      label="URL Photo"
                      value={presenter.image}
                      onChange={(e) => handlePresenterChange(index, 'image', e.target.value)}
                      placeholder="https://..."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Paper>

        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            type="submit"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            disabled={saving}
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/replays')}>
            Annuler
          </Button>
        </Box>
      </form>
    </Box>
  );
}
